import { apiClient } from '../api/client';

export interface RegisterPlayerData {
  name: string;
  email: string;
  password: string;
  /** Sem nick na loja de produtos digitais: a API gera um a partir do e-mail. */
  hytaleNick?: string;
  birthDate?: string;
}

/** Nick na loja de jogo; e-mail na loja de produtos digitais. */
export type LoginPlayerData = { nick: string; password: string } | { email: string; password: string };

export interface PlayerRole {
  id: string;
  name: string;
  isStaff: boolean;
  permissions: string[];
  userCss?: string;
  badgeCss?: string;
}

export interface PlayerInfo {
  id: string;
  storeId: string;
  name: string;
  email: string;
  nick: string;
  credits: number;
  role: PlayerRole | null;
}

/** Response from login — tokens are in HttpOnly cookies, not in the body */
export interface PlayerAuthResponse {
  player: PlayerInfo;
}

export const playerAuthService = {
  async register(storeId: string, data: RegisterPlayerData) {
    return apiClient.post<{ id: string; name: string; email: string; nick: string; credits: number }>(
      `/public/stores/${storeId}/players`,
      { name: data.name, email: data.email, password: data.password, nick: data.hytaleNick, birthDate: data.birthDate },
    );
  },

  /** Pede o link de redefinição. A resposta é a mesma exista ou não a conta. */
  async forgotPassword(storeId: string, email: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      `/public/stores/${storeId}/players/forgot-password`,
      { email },
    );
  },

  /** Define a senha nova com o token recebido por e-mail. */
  async resetPassword(storeId: string, token: string, password: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      `/public/stores/${storeId}/players/reset-password`,
      { token, password },
    );
  },

  async login(storeId: string, data: LoginPlayerData): Promise<PlayerAuthResponse> {
    // Tokens arrive as HttpOnly cookies — only player metadata in the response body
    return apiClient.post<PlayerAuthResponse>(
      `/public/stores/${storeId}/players/login`,
      data,
    );
  },

  async getMyProfile(storeId: string) {
    return apiClient.get<{
      player: {
        id: string;
        name: string;
        email: string;
        nick: string;
        credits: number;
        status: string;
        role: PlayerRole | null;
      };
    }>(`/public/stores/${storeId}/players/me`);
  },

  async getPlayerData(storeId: string, playerId: string) {
    return apiClient.get<{
      player: {
        id: string;
        name: string;
        email: string;
        nick: string;
        creditsBalance: number;
        status: string;
        role?: string;
      };
    }>(`/stores/${storeId}/players/${playerId}`);
  },

  async changePassword(storeId: string, data: { currentPassword: string; newPassword: string; confirmPassword: string }) {
    return apiClient.put<{ success: boolean; message: string }>(
      `/public/stores/${storeId}/player/profile/password`,
      data,
    );
  },

  async changeEmail(storeId: string, data: { newEmail: string; password: string }) {
    return apiClient.put<{
      success: boolean;
      message: string;
      player: { id: string; email: string; updatedAt: Date };
    }>(`/public/stores/${storeId}/player/profile/email`, data);
  },

  /** Persist only non-sensitive player metadata (no tokens) */
  saveAuth(authData: PlayerAuthResponse) {
    localStorage.setItem('playerStoreId', authData.player.storeId);
    localStorage.setItem('player', JSON.stringify(authData.player));
  },

  /** Update cached player metadata without touching tokens */
  savePlayer(player: PlayerInfo) {
    localStorage.setItem('playerStoreId', player.storeId);
    localStorage.setItem('player', JSON.stringify(player));
  },

  getPlayer(): PlayerInfo | null {
    const playerStr = localStorage.getItem('player');
    return playerStr ? JSON.parse(playerStr) : null;
  },

  getStoreId(): string | null {
    return localStorage.getItem('playerStoreId');
  },

  /**
   * O jogador apaga a própria conta.
   *
   * A linha não sai do banco: o que sai é o dado pessoal, e a conta fica
   * inativa. Pedido e transação de crédito continuam existindo por obrigação
   * contábil e apontam para ela. Conta com saldo é recusada pela API com
   * `HAS_CREDITS` e o valor, para a tela dizer quanto falta gastar.
   */
  async deleteAccount(storeId: string) {
    return apiClient.delete<void>(`/public/stores/${storeId}/player/profile`);
  },

  async logout(storeId?: string) {
    const sid = storeId ?? this.getStoreId();
    if (sid) {
      try {
        await apiClient.post(`/public/stores/${sid}/players/logout`, {});
      } catch {
        // Proceed with local cleanup even if API call fails
      }
    }
    localStorage.removeItem('playerStoreId');
    localStorage.removeItem('player');
  },

  isAuthenticated(): boolean {
    // Optimistic: assumes cached player data implies a valid cookie.
    // Any 401 from the server will trigger the auto-refresh or redirect via the axios interceptor.
    return !!this.getPlayer();
  },

  async heartbeat(storeId: string) {
    try {
      await apiClient.post(`/stores/${storeId}/forum/topics/heartbeat`, {});
      return true;
    } catch {
      return false;
    }
  },
};
